import * as React from "react";
import { connect } from "react-redux";
import { Route, Redirect, RouteComponentProps, withRouter } from "react-router-dom";
import moize from "moize";
import withTracker from "./withTracker";
import { fetchApi } from "../services/api";
import { ApplicationState }  from '../store';
import * as AuthenticationState from '../store/authentication';
import facilityBreadcrumb from "./Breadcrumbs/facility";
import accountBreadcrumb from "./Breadcrumbs/account";
import childBreadcrumb from "./Breadcrumbs/child";
import classroomBreadcrumb from "./Breadcrumbs/classroom";
import instructorBreadcrumb from "./Breadcrumbs/instructor";

import { Layout } from "./Layout";
import Home from "./Home";
import Users from "./Users";
import ChangePassword from "./Users/ChangePassword";
import UserProfile from "./Users/Profile";
import TenantProfile from "./Users/TenantProfile";
import Accounts from "./Accounts";
import AccountProfile from "./Accounts/Profile";
import AccountChildren from "./Accounts/Children";
import AccountPayments from "./Accounts/Payments";
import AccountInvoices from "./Accounts/Invoices";
import AccountInvoicesPreview from "./Accounts/InvoicesPreview";
import AccountReceipts from "./Accounts/Receipts";
import AccountReceiptsPreview from "./Accounts/ReceiptsPreview";
import AccountAgreements from "./Accounts/Agreements";
import AccountDocuments from "./Accounts/Documents";
import AccountAuditLog from "./Accounts/AuditLog";
import Facilities from './Facilities';
import Facility from './Facilities/Profile';
import Classrooms from "./Classrooms";
import Classroom from "./Classrooms/Profile";
import ClassroomChildren from "./Classrooms/Children";
import ClassroomSchedule from "./Classrooms/Schedule";
import Children from "./Children";
import ChildProfile from "./Children/Profile";
import ChildGuardians from "./Children/ChildGuardians";
import ChildReleases from "./Children/ChildReleases";
import ChildMedical from "./Children/ChildMedical";
import ChildAcademic from "./Children/Academic";
import ChildNotes from "./Children/ChildNotes"
import ChildRates from "./Children/Rates";
import ChildSchedule from "./Children/Schedule";
import ChildScheduleActivities from "./Children/ChildScheduleActivities";
import ChildDocuments from "./Children/Documents";
import ChildAuditLog from "./Children/AuditLog";
import ChildPrint from "./Children/Print";
import ChildCharges from "./Children/Charges";
import ChildAttendances from "./Children/Attendances";
import ChildClassrooms from "./Children/ChildClassrooms";
import ChildCopy from "./Children/Copy";
import Attendance from "./Attendance";
import AttendancePrint from "./Attendance/Print";
import Holidays from "./Holidays";
import Invoices from "./Invoices/index";
import Invoice from "./Accounts/Invoice";
import BillingPeriods from "./Invoices/BillingPeriods";
import BillingPeriod from "./Invoices/BillingPeriod";
import Receipts from "./Receipts";
import Receipt from "./Accounts/Receipt";
import Notices from "./Notices";
import DocumentUpload from "./Documents/Upload";
import DocumentEdit from "./Documents/Edit";
import Agreements from "./Agreements";
import Agreement from "./Agreements/Agreement";
import ExpiringRates from "./Reports/ExpiringRates";
import AttendanceReport from "./Reports/AttendanceReport";
import Payments from "./Reports/Payments";
import OutstandingBalances from "./Reports/OutstandingBalances";
import UpcomingBirthdays from "./Reports/UpcomingBirthdays";
import RetirableAccounts from "./Reports/RetirableAccounts";
import Forms from "./Forms";
import MobileGlobalSearch from "./MobileGlobalSearch";
import Instructor from "./Instructors/Profile";
import Instructors from "./Instructors";
import Teachers from "./Teachers";
import Teacher from "./Teachers/Profile";
import teacherBreadcrumb from "./Breadcrumbs/teacher";
import Schools from "./Schools";
import School from "./Schools/Profile";
import schoolBreadcrumb from "./Breadcrumbs/school";
import Documents from "./Documents";
import CalendarAttendance from "./Reports/CalendarAttendance";
import TenantCreate from "./Tenant/Create";

// At runtime, Redux will merge together...
type PrivateRoutesProps =
    AuthenticationState.AuthenticationState         // ... state we've requested from the Redux store
    & typeof AuthenticationState.actionCreators     // ... plus action creators we've requested
    & RouteComponentProps<{ }>;                     // ... plus incoming routing parameters

class PrivateRoutes extends React.Component<PrivateRoutesProps, {}> {
  public render() {
    if (!this.props.token) {
        return <Redirect to='/' />;
    }
    return (
        <Layout history={this.props.history} >
            <Route exact path='/home' component={ withTracker(Home) } />
            <Route exact path='/users' component={ withTracker(Users) } />
            <Route exact path='/user/changePassword' component={ withTracker(ChangePassword) } />
            <Route exact path='/user/:userId?/profile' component={ withTracker(UserProfile) } />
            <Route exact path='/company/profile' component={ withTracker(TenantProfile) } />
            <Route exact path='/accounts' component={ withTracker(Accounts) } />
            <Route exact path='/accounts/:accountId' component={ withTracker(accountBreadcrumb(AccountProfile, fetchAccountBreadcrumb)) } />
            <Route exact path='/accounts/:accountId/children' component={ withTracker(accountBreadcrumb(AccountChildren, fetchAccountBreadcrumb, { page: "Children" })) } />
            <Route exact path='/accounts/:accountId/payments' component={ withTracker(accountBreadcrumb(AccountPayments, fetchAccountBreadcrumb, { page: "Payments" })) } />
            <Route exact path='/accounts/:accountId/invoices' component={ withTracker(accountBreadcrumb(AccountInvoices, fetchAccountBreadcrumb, { page: "Invoices" })) } />
            <Route exact path='/accounts/:accountId/invoices/:billingPeriodId' component={ withTracker(AccountInvoicesPreview, fetchAccountBreadcrumb) } />
            <Route exact path='/accounts/:accountId/receipts' component={ withTracker(accountBreadcrumb(AccountReceipts, fetchAccountBreadcrumb, { page: "Receipts" })) } />
            <Route exact path='/accounts/:accountId/receipts/:year/' component={ withTracker(AccountReceiptsPreview, fetchAccountBreadcrumb) } />
            <Route exact path='/accounts/:accountId/agreements' component={ withTracker(accountBreadcrumb(AccountAgreements, fetchAccountBreadcrumb, { page: "Agreements" })) } />
            <Route exact path='/accounts/:accountId/documents' component={ withTracker(accountBreadcrumb(AccountDocuments, fetchAccountBreadcrumb, { page: "Documents" })) } />
            <Route exact path='/accounts/:accountId/logs' component={ withTracker(accountBreadcrumb(AccountAuditLog, fetchAccountBreadcrumb, { page: "Logs" })) } />
            <Route exact path='/facilities' component={ withTracker(Facilities) } />
            <Route exact path='/facility/:facilityId' component={withTracker(facilityBreadcrumb(Facility))} />
            <Route exact path='/classrooms' component={withTracker(Classrooms)} />
            <Route exact path='/classroom/:classroomId' component={withTracker(classroomBreadcrumb(Classroom))} />
            <Route exact path='/classroom/:classroomId/children' component={withTracker(classroomBreadcrumb(ClassroomChildren, { page: "Children"}))} />
            <Route exact path='/classroom/:classroomId/schedule' component={withTracker(classroomBreadcrumb(ClassroomSchedule, { page: "Schedule"}))} />
            <Route exact path='/children' component={ withTracker(Children) } />
            <Route exact path='/child/:childId' component={ withTracker(childBreadcrumb(ChildProfile, fetchChildBreadcrumb)) } />
            <Route exact path='/child/:childId/guardians' component={withTracker(childBreadcrumb(ChildGuardians, fetchChildBreadcrumb, { page: "Guardians" }))} />
            <Route exact path='/child/:childId/releases' component={ withTracker(childBreadcrumb(ChildReleases, fetchChildBreadcrumb, { page: "Releases" }))} />
            <Route exact path='/child/:childId/medical' component={ withTracker(childBreadcrumb(ChildMedical, fetchChildBreadcrumb, { page: "Medical" })) } />
            <Route exact path='/child/:childId/academic' component={ withTracker(childBreadcrumb(ChildAcademic, fetchChildBreadcrumb, { page: "Academic" })) } />
            <Route exact path='/child/:childId/notes' component={ withTracker(childBreadcrumb(ChildNotes, fetchChildBreadcrumb, { page: "Notes" })) } />
            <Route exact path='/child/:childId/rates' component={ withTracker(childBreadcrumb(ChildRates, fetchChildBreadcrumb, { page: "Rates" })) } />
            <Route exact path='/child/:childId/schedule' component={ withTracker(childBreadcrumb(ChildSchedule, fetchChildBreadcrumb, { page: "Schedule" })) } />
            <Route exact path='/child/:childId/schedule/:childScheduleId/activities' component={ withTracker(childBreadcrumb(ChildScheduleActivities, fetchChildBreadcrumb, { page: "Schedule" })) } />
            <Route exact path='/child/:childId/documents' component={ withTracker(childBreadcrumb(ChildDocuments, fetchChildBreadcrumb, { page: "Documents" })) } />
            <Route exact path='/child/:childId/logs' component={ withTracker(childBreadcrumb(ChildAuditLog, fetchChildBreadcrumb, { page: "Logs" })) } />
            <Route exact path='/child/:childId/print' component={ withTracker(ChildPrint) } />
            <Route exact path='/child/:childId/charges' component={ withTracker(childBreadcrumb(ChildCharges, fetchChildBreadcrumb, { page: "Charges" })) } />
            <Route exact path='/child/:childId/attendance' component={withTracker(childBreadcrumb(ChildAttendances, fetchChildBreadcrumb, { page: "Attendance" }))} />
            <Route exact path='/child/:childId/classrooms' component={withTracker(childBreadcrumb(ChildClassrooms, fetchChildBreadcrumb, { page: "Classroom" }))} />
            <Route exact path='/child/:childId/copy' component={withTracker(childBreadcrumb(ChildCopy, fetchChildBreadcrumb, { page: "Copy" }))} />
            <Route exact path='/instructors' component={withTracker(Instructors)} />
            <Route exact path='/instructor/:instructorId' component={withTracker(instructorBreadcrumb(Instructor))} />
            <Route exact path='/attendance' component={ withTracker(Attendance) } />
            <Route exact path='/attendance/print' component={ withTracker(AttendancePrint) } />
            <Route exact path='/holidays' component={ withTracker(Holidays) } />
            <Route exact path='/invoices' component={ withTracker(Invoices) } />
            <Route exact path='/invoice/:invoiceId' component={ withTracker(Invoice) } />
            <Route exact path='/invoices/periods' component={ withTracker(BillingPeriods) } />
            <Route exact path='/invoices/period/:periodId' component={ withTracker(BillingPeriod) } />
            <Route exact path='/receipts' component={ withTracker(Receipts) } />
            <Route exact path='/receipt/:taxReceiptId' component={ withTracker(Receipt) } />
            <Route exact path='/notices' component={ withTracker(Notices) } />
            <Route exact path='/documents/upload' component={ withTracker(DocumentUpload) } />
            <Route exact path='/document/:documentId' component={ withTracker(DocumentEdit) } />
            <Route exact path='/agreements' component={ withTracker(Agreements) } />
            <Route exact path='/agreement/:agreementId/:agreementVersion' component={withTracker(Agreement)} />
            <Route exact path='/reports/attendanceReport' component={withTracker(AttendanceReport)} />
            <Route exact path='/reports/calendarAttendance' component={withTracker(CalendarAttendance)} />
            <Route exact path='/reports/expiringRates' component={ withTracker(ExpiringRates) } />
            <Route exact path='/reports/payments' component={ withTracker(Payments) } />
            <Route exact path='/reports/outstandingBalances' component={ withTracker(OutstandingBalances) } />
            <Route exact path='/reports/upcomingBirthdays' component={ withTracker(UpcomingBirthdays) } />
            <Route exact path='/reports/retirableAccounts' component={ withTracker(RetirableAccounts) } />
            <Route exact path='/forms' component={ withTracker(Forms) } />
            <Route exact path='/search' component={ withTracker(MobileGlobalSearch) } />
            <Route exact path='/teachers' component={ withTracker(Teachers) } />
            <Route exact path='/teacher/:teacherId' component={ withTracker(teacherBreadcrumb(Teacher))} />
            <Route exact path='/schools' component={ withTracker(Schools) } />
            <Route exact path='/school/:schoolId' component={ withTracker(schoolBreadcrumb(School))} />
            <Route exact path='/documents' component={ withTracker(Documents) } />
            <Route exact path='/tenant/create' component={ TenantCreate } />
            
        </Layout>
    );
  }
}

const fetchChildBreadcrumb = moize((childId: number) => {
    if (childId == 0) {
        return Promise.resolve({ accountName: "New Child" });
    } else {
        return fetchApi(`/api/Breadcrumb/Child/${childId}`)
            .then((response: { ChildID: number, ChildName: string, AccountID: number, AccountName: string, Links: ApiLink[] }) => {
                return {
                    accountId: response.AccountID,
                    accountName: response.AccountName,
                    childId: response.ChildID,
                    childName: response.ChildName,
                    links: response.Links
                };
            })
    }
});

const fetchAccountBreadcrumb = moize((accountId: number) => {
    if (accountId == 0) {
        return Promise.resolve({ accountName: "New Account" });
    } else {
        return fetchApi(`/api/Breadcrumb/Account/${accountId}`)
            .then((response: { AccountID: number, AccountName: string, Balance: number, Links: ApiLink[] }) => {
                return {
                    accountId: response.AccountID,
                    accountName: response.AccountName,
                    balance: response.Balance,
                    links: response.Links
                };
            })
    }
});

export default withRouter(connect(
    (state: ApplicationState) => state.authentication, // Selects which state properties are merged into the component's props
    AuthenticationState.actionCreators                 // Selects which action creators are merged into the component's props
)(PrivateRoutes));
